import { Box, Stack } from "@mui/material";
import { DamsAppAsset } from "../../assets/registry";
import { MainPageBase } from "../page-bases/MainPageBase";
import { useEffect, useState } from "react";
import { DamsEmptyPageProps } from "../page-bases";
import DamsTable from "../../components/Table/CollapsibleTable";
import { NoItemContent } from "../page-bases/NoItemContent";
import { TRowItem } from "../../components/Table/TableRow";
import { rolesApi } from "../../services/roles";
import { IPagination, PageActions, Role, Roles, IQueryPerams } from "../../config/types";
import { usePageModels } from "../../hooks/usePageActions";
import ModalItemInput from "../../components/Input/ModalItemInput";
import { ModalBase } from "../../components/Modals/ModalBase";
import ActionModal from "../../components/Modals/ActionModal";
import { usePagination } from "../../hooks/usePagination";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { BadgeLabel } from "../../components/Label";
import { PermissionSelectInput } from "../../common-ui/select-input-models/PermissionSelectInput";
import { RoleLogsFilter } from "../../common-ui/page-filters/RoleLogsFilter";
import { formatDate } from "../../utils/common";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import { useMangeTableItems } from "../../hooks/useManageTableItems";

interface PermissionItem{
  name:string;
  id:string;
}

interface FiltersState {
  search?: string;
  role_name?: string;
  is_deleted: boolean;
}

function RolesMainPage() {
  const [pageState, setPageState] = useState("main");
  const [emptyState, setEmptyState] = useState<DamsEmptyPageProps>();
  const [dataset, setDataset] = useState<TRowItem[]>([]);

  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();

  const [currentFilters, setCurrentFilters] = useState<FiltersState>({
    is_deleted: false
  });

  const {
    form,
    updateForm,
    updateInputField,
    setSubmitAttempt,
    onFormSubmit,
    resetForm,
    fieldHasError
  } = useDmsForm<Role>({
    role_name: ""
  });
  const [isProcessing, setProcess] = useState(false);
  const [selectedPermissions, updateSelectedPermissions] = useState<
  TRowItem[]
  >([]);
  
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState
  } = usePagination<any>(rolesApi.getRoles);

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems
  } = useMangeTableItems(dataset);

  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  const populateDataSet = (data: Roles[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      created_at: formatDate(data.created_at),
      updated_at: data.updated_at ? formatDate(data.updated_at) : 'Not Yet',
      permission_names:
        data.permissions? data.permissions.map(
              (item) => ` ${item.name} `
            ) : []
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  }

  const fetchData = async (query: IQueryPerams = {}) => {
    try {
      const result = await rolesApi.getRoles({...query, is_deleted: false });
      if (result?.data) {
        populateDataSet(result.data, result.pagination);
      }
    } catch (err) {
      setEmptyState({ type: "empty-db" });
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page_info = getEmptyPageInfo(pageState);

  async function handleAddRequest() {
    setSubmitAttempt(true);
    const { data, isValid } = onFormSubmit({
      excludeFields: ["permission_ids"]
    });
    if (isValid) {
      setProcess(true);
      const requestBody = {
        ...data,
        permission_ids: selectedPermissions.map((item) => item.id)
      };
      const request =
        pageAction?.type === "add"
          ? rolesApi.createRole(requestBody)
          : rolesApi.updateRole(pageAction?.id ?? "", requestBody);
      const res = await request;
      if (res.success) {
        fetchData(querySet);
      }
      setProcess(false);
      updatePageAction({
        type: pageAction?.type,
        message: res.message,
        title: pageAction?.type === "edit" ? "Update Role" : "New Role",
        success: res.success
      });
      openModel("alert");
      resetForm();
    }
  }

  function addNewItem() {
    resetForm();
    updateSelectedPermissions([])
    openModel("add");
    updatePageAction({
      type: "add"
    });
  }

  function openDeleteModel(item: TRowItem) {
    openModel("delete");
    updatePageAction({
      id: item.id,
      name: item.name
    });
  }

  function updateRole(item: TRowItem) {
    updateSelectedPermissions((item.permissions as PermissionItem[]).map(value=>({id: value.id,name:value.name})));
    updateForm({
      role_name: {
        value: item.name,
        isValid: true
      }
    });
    openModel("add");
    updatePageAction({
      id: item.id,
      name: item.name,
      type: "edit"
    });
  }

  async function handleDeleteRequest() {
    setProcess(true);
    const res = await rolesApi.deleteRole(pageAction?.id ?? "");
    if (res.success) {
      fetchData(querySet);
    }
    setProcess(false);
    updatePageAction({
      title: res.success ? "Delete Role" : "Delete Role Failed",
      message: res.message,
      success: res.success
    });
    openModel("alert");
  }

  function toggleAlertModal() {
    if (pageAction?.type === "add") {
      openModel("add");
    } else {
      closeModel();
    }
  }

  function handlePermissionSelect(items: TRowItem[]) {
    updateSelectedPermissions(items);
  }

  function removePermission(permission_id: string) {
    updateSelectedPermissions((prev) => prev.filter((item) => item.id !== permission_id));
  }

  function clearSearch() {
    setPageState("empty-db");
  }

  const renderHeaderDropDownMenu = ({
    closeDialog
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData(querySet);
      closeDialog();
    };
    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={rolesApi.deleteRole}
          idKey={"id"}
        />
      </Stack>
    );
  };

  return (
    <MainPageBase
      title={"Roles"}
      subtitle={""}
      pageState={pageState}
      addItemBtnOnClick={addNewItem}
      addItemBtnText={"Add Role"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={page_info.title}
            subtitle={page_info.message}
            addItemBtn={() => addNewItem()}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"} spacing={1}>
            <RoleLogsFilter<Roles[]>
              ftn={rolesApi.getRoles}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />

            <DamsTable
              tableTitle="Roles"
              tableSubtitle=""
              numTableRecords="25"
              tableHeaders={columns}
              tableKeys={tkeys}
              data={dataset}
              onTableItemEdit={updateRole}
              onTableItemDelete={openDeleteModel}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              headerDropDownMenu={renderHeaderDropDownMenu}
              tableTitleBadge={selectedRowItems.length === 0 ? undefined : selectedRowItems.length + ' selected'}
            />

            {/* Add Modal */}
            <ModalBase
              open={models.showAddModel}
              title={pageAction?.type === "add" ? "Add New Role" : "Edit Role"}
              width={480}
              subtitle={""}
              type={"add-item"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.Programmes}
              leftBtnTxt="Cancel"
              onLeftBtnPress={closeModel}
              rightBtnTxt={
                pageAction?.type === "add" ? "Add Role" : "Edit Role"
              }
              onRightBtnPress={handleAddRequest}
              onRightBtnLoading={isProcessing}
              onClose={closeModel}
            >
              <Stack gap={4}>
                <ModalItemInput
                  label="Role"
                  placeholder="Enter Role Name"
                  handleChange={updateInputField("role_name", "none")}
                  value={form.role_name.value}
                  error={fieldHasError("role_name")}
                  errorMessage={form.role_name.errorMessage}
                />
                <PermissionSelectInput
                  placeholder="Pick a permission "
                  label="Permissions"
                  handleChange={handlePermissionSelect}
                  value={selectedPermissions}
                />
              </Stack>
              <Box minHeight={50} marginTop={3} width={380}>
                {selectedPermissions.map((item, index) => (
                  <BadgeLabel
                    key={index}
                    margin={0.5}
                    onClick={() => removePermission(item.id)}
                    title={item.name}
                    colorType="blue"
                    isDeleteAction
                  />
                ))}
              </Box>
            </ModalBase>

            <ModalBase
              open={models.showDeleteModel}
              title={"Delete Role"}
              subtitle={
                <Box>
                  Once you delete{" "}
                  <span style={{ fontWeight: "bold" }}>{pageAction?.name}</span>
                  , all related information will be lost forever. Are you sure
                  you want to proceed?
                </Box>
              }
              type={"action"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.delete_icon}
              leftBtnTxt="Cancel"
              onLeftBtnPress={closeModel}
              rightBtnTxt="Delete"
              onRightBtnPress={handleDeleteRequest}
              onRightBtnLoading={isProcessing}
              onClose={closeModel}
            />

            <ActionModal
              show={models.showAlertModel}
              title={pageAction?.title ?? ""}
              leftIcon={
                pageAction?.success === false
                  ? DamsAppAsset.icons.warning
                  : undefined
              }
              subtitle={pageAction?.message ?? ""}
              onLeftBtnPress={closeModel}
              leftBtnTxt={"close"}
              onRightBtnPress={toggleAlertModal}
              rightBtnTxt={pageAction?.type === "add" ? "Add New" : "continue"}
              onClosePress={closeModel}
            />
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
}

const getEmptyPageInfo = (key: string): { title: string; message: string } => {
  if (page_msg.hasOwnProperty(key)) {
    return page_msg[key];
  } else {
    return { title: "", message: "" };
  }
};

const page_msg: { [key: string]: { title: string; message: string } } = {
  "empty-db": {
    title: "No Roles Found",
    message:
      'No Roles have been added yet. Click the "Add New" button to add a new Role.'
  },

  "empty-search": {
    title: "No Roles Found",
    message:
      "No Roles found in your database. Please add a new Role to find them in the Roles tab"
  }
};

const tkeys = ["name", "permissions_count", "created_at"];
const columns = ["Role Name", "No. of Permissions", "Date Added"];
const hkeys = ["permission_names"];
const hcolumns = ["Permissions"];

export default RolesMainPage;